'use client';

import { createContext, useContext, useState } from 'react';

import { setTranslations, setActiveCountry, setUrlLocale, createI18nContext } from '../i18nService';

const I18nContext = createContext<ReturnType<typeof createI18nContext> | null>(null);

export default function I18nProvider({
  children,
  locale,
  urlLocale,
  messages,
}: Readonly<{
  children?: React.ReactNode;
  locale: `${string}-${string}`;
  urlLocale: string;
  messages: Record<string, string>;
}>) {
  const [lang, country] = locale.split('-');
  /**
   * Using useState is kind of a hack to initialize immediately the client modules
   * with data coming from the server.
   * Initialize client configuration to use like this:
   * @example
   * import { getActiveLang } from '@openclassrooms/translations';
   * const lang = getActiveLang();
   */
  useState(() => {
    setTranslations(lang, messages);
    setActiveCountry(country);
    setUrlLocale(urlLocale);
  });

  return (
    <I18nContext.Provider
      value={createI18nContext({
        lang,
        locale,
        country,
        messages,
      })}
    >
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = (): ReturnType<typeof createI18nContext> => {
  const context = useContext(I18nContext);
  if (context == null) {
    throw new Error('useI18n hook was used without I18nProvider.');
  }

  return context;
};
